import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  User,
  ArrowLeft,
  MessageSquare,
  MapPin,
  Briefcase,
  Calendar,
  Mail,
  Building2,
  Globe,
  Shield,
  Lock,
  Send,
  Clock,
  CheckCircle
} from "lucide-react";

const UserProfile = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [showMessageBox, setShowMessageBox] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/users/profile/${userId}`, {
        credentials: "include"
      });
      
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
      } else if (res.status === 403) {
        setProfile({ private: true });
      } else {
        toast.error("Failed to load profile");
        navigate(-1);
      }
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!messageContent.trim()) {
      toast.error("Please enter a message");
      return;
    }

    setSendingMessage(true);
    try {
      const res = await fetch(`${API}/messages/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          recipient_id: userId,
          content: messageContent
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        if (data.status === "pending_approval") {
          toast.success("Message request sent! Waiting for approval.");
        } else {
          toast.success("Message sent!");
          navigate("/messages");
        }
        setMessageContent("");
        setShowMessageBox(false);
      } else {
        toast.error(data.detail || "Failed to send message");
      }
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (profile?.private) {
    return (
      <div className="min-h-screen bg-obsidian">
        <header className="fixed top-0 w-full z-50 bg-obsidian/80 backdrop-blur-md border-b border-[#222]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
            <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white flex items-center gap-2">
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
          </div>
        </header>
        
        <main className="pt-24 pb-12 px-4 sm:px-6 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="w-24 h-24 bg-obsidian-light border border-[#333] flex items-center justify-center mx-auto mb-6">
              <Lock className="w-12 h-12 text-gray-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-4">Private Profile</h1>
            <p className="text-gray-400">This user has set their profile to private.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian" data-testid="user-profile-page">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-obsidian/80 backdrop-blur-md border-b border-[#222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white flex items-center gap-2">
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          {!profile?.is_own_profile && (
            <Button
              onClick={() => setShowMessageBox(!showMessageBox)}
              className="bg-crimson hover:bg-crimson/90"
              data-testid="send-message-button"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Send Message
            </Button>
          )}
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          {/* Profile Header */}
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              {/* Avatar */}
              <div className="w-24 h-24 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                {profile?.picture ? (
                  <img 
                    src={profile.picture} 
                    alt={profile.name} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-12 h-12 text-crimson" />
                )}
              </div>
              
              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="font-heading text-2xl font-bold text-white">
                    {profile?.name || "Unnamed User"}
                  </h1>
                  <span className={`px-2 py-0.5 text-xs uppercase ${
                    profile?.role === "recruiter" 
                      ? "bg-blue-500/20 text-blue-400"
                      : profile?.role === "admin"
                        ? "bg-yellow-500/20 text-yellow-400"
                        : "bg-green-500/20 text-green-400"
                  }`}>
                    {profile?.role}
                  </span>
                  {profile?.privacy_settings?.message_approval === "approval_required" && (
                    <span className="flex items-center gap-1 text-yellow-400 text-xs">
                      <Shield className="w-3 h-3" />
                      Approval Required
                    </span>
                  )}
                </div>
                
                {profile?.profile?.headline && (
                  <p className="text-gray-400 mb-2">{profile.profile.headline}</p>
                )}
                
                {profile?.profile?.company_name && (
                  <p className="text-gray-400 mb-2 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    {profile.profile.company_name}
                  </p>
                )}
                
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  {profile?.profile?.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {profile.profile.location}
                    </span>
                  )}
                  {profile?.created_at && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Joined {new Date(profile.created_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Message Box */}
            {showMessageBox && !profile?.is_own_profile && (
              <motion.div
                className="mt-6 pt-6 border-t border-[#333]"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
              >
                <h3 className="font-heading font-bold mb-3">Send a Message</h3>
                
                {profile?.message_approval_required && (
                  <div className="mb-3 p-3 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-sm flex items-start gap-2">
                    <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>This user requires approval before receiving messages. Your message request will be sent for approval.</span>
                  </div>
                )}
                
                <textarea
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  placeholder="Write your message..."
                  rows={3}
                  className="w-full px-4 py-2 bg-obsidian border border-[#333] text-white resize-none mb-3"
                />
                
                <div className="flex justify-end gap-2">
                  <Button
                    onClick={() => setShowMessageBox(false)}
                    variant="outline"
                    className="border-[#333] text-white"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={sendMessage}
                    disabled={sendingMessage || !messageContent.trim()}
                    className="bg-crimson hover:bg-crimson/90"
                  >
                    {sendingMessage ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    {profile?.message_approval_required ? "Send Request" : "Send Message"}
                  </Button>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Profile Content */}
          {profile?.profile && (
            <motion.div
              className="bg-obsidian-light border border-[#222] p-6 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              {/* Summary / Company Description */}
              {(profile.profile.summary || profile.profile.company_description) && (
                <div className="mb-6">
                  <h3 className="font-heading font-bold text-white mb-3">
                    {profile.role === "recruiter" ? "About Company" : "About"}
                  </h3>
                  <p className="text-gray-400 whitespace-pre-line">
                    {profile.profile.summary || profile.profile.company_description}
                  </p>
                </div>
              )}

              {/* Skills */}
              {profile.profile.skills && profile.profile.skills.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-heading font-bold text-white mb-3">Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.profile.skills.map(skill => (
                      <span key={skill} className="px-3 py-1 bg-obsidian text-gray-300 border border-[#333]">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience */}
              {profile.profile.experience && profile.profile.experience.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-heading font-bold text-white mb-3">Experience</h3>
                  <div className="space-y-4">
                    {profile.profile.experience.map((exp, i) => (
                      <div key={i} className="border-l-2 border-crimson pl-4">
                        <h4 className="font-bold text-white">{exp.title}</h4>
                        <p className="text-gray-400">{exp.company}</p>
                        <p className="text-sm text-gray-500">
                          {exp.start_date} - {exp.end_date || "Present"}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Website */}
              {profile.profile.website && (
                <div className="flex items-center gap-2 text-gray-400">
                  <Globe className="w-4 h-4" />
                  <a href={profile.profile.website?.startsWith("http") ? profile.profile.website : `https://${profile.profile.website}`} target="_blank" rel="noopener noreferrer" className="hover:text-crimson transition-colors">
                    {profile.profile.website}
                  </a>
                </div>
              )}
            </motion.div>
          )}

          {/* Active Jobs (for recruiters) */}
          {profile?.active_jobs && profile.active_jobs.length > 0 && (
            <motion.div
              className="bg-obsidian-light border border-[#222] p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="font-heading font-bold text-white mb-4">Active Job Postings</h3>
              <div className="space-y-3">
                {profile.active_jobs.map(job => (
                  <Link
                    key={job.job_id}
                    to={`/jobs/${job.job_id}`}
                    className="block p-4 bg-obsidian border border-[#333] hover:border-crimson/30 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-bold text-white">{job.title}</h4>
                        <p className="text-sm text-gray-500 flex items-center gap-2">
                          <MapPin className="w-3 h-3" />
                          {job.location}
                        </p>
                      </div>
                      <Briefcase className="w-5 h-5 text-gray-600" />
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default UserProfile;
